import {
  Calendar,
  Clock,
  Video,
  ClipboardList,
  ListChecks,
  MessagesSquare,
  Notebook,
  Watch,
  Users,
  FileText,
  BookHeart,
  Mic,
  ArrowRight,
} from "lucide-react";
import { selectCompensation } from "../lib/countries";
import { formatClosingDate } from "../lib/dates";
import CompensationStack from "./CompensationStack";

// Pick an icon + accessible label for each research type.
const formatMeta = (raw) => {
  const f = (raw || "").toLowerCase();
  if (f.includes("survey")) return { Icon: ClipboardList, label: raw || "Survey" };
  if (f.includes("bulletin") || f.includes("discussion board"))
    return { Icon: MessagesSquare, label: raw || "Bulletin board" };
  if (f.includes("group"))
    return { Icon: Users, label: raw || "Group" };
  if (f.includes("pre-task") || f.includes("post-task") || f.includes("pretask") || f.includes("posttask"))
    return { Icon: ListChecks, label: raw };
  if (f.includes("diary") || f.includes("check-in"))
    return { Icon: Notebook, label: raw };
  if (f.includes("material") || f.includes("feedback"))
    return { Icon: FileText, label: raw };
  if (f.includes("journey")) return { Icon: BookHeart, label: raw };
  if (f.includes("wearable") || f.includes("app"))
    return { Icon: Watch, label: raw };
  if (f.includes("interview")) return { Icon: Mic, label: raw || "Interview" };
  return { Icon: Video, label: raw || "Research" };
};

// ============================================================
// StudyCard , patient-facing card on /patient/studies,
// /caregiver/studies, /research/:slug and the homepage.
// Order of vertical info (per Feb 2026 spec):
//   1. Condition pill
//   2. Research types (one or more pills)
//   3. Synopsis (clamped)
//   4. Time commitment
//   5. Confirmation of diagnosis
//   6. Closing date
//   7. Compensation (stacked by country , flag · short name · amount)
//   8. Find out more
// ============================================================
export const StudyCard = ({
  study,
  onRegister,
  variant = "dark",
  viewerCountry = null,
}) => {
  const isLight = variant === "light";
  const types =
    Array.isArray(study.research_types) && study.research_types.length > 0
      ? study.research_types
      : study.format
        ? [study.format]
        : [];

  const { rows: compRows, fallback: compFallback } = selectCompensation(
    study,
    viewerCountry,
  );

  return (
    <button
      type="button"
      onClick={() => onRegister?.(study)}
      data-testid={`study-card-${study.id}`}
      className={`study-card text-left w-full flex flex-col h-full p-6 md:p-7 rounded-2xl transition-all duration-300 group ${
        isLight
          ? "bg-white border border-[var(--light-border)] hover:border-[var(--teal)] hover:shadow-[0_16px_40px_-20px_rgba(5,150,105,0.35)]"
          : "bg-[var(--dark-card)] border border-[var(--dark-border)] hover:border-[var(--lime)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.4)]"
      }`}
    >
      {/* 1. Condition badge gets the full card width so long names
          (e.g. "Non-small cell lung cancer (NSCLC)") don't have to
          compete with the ref tag. The ref drops to a small line
          beneath the badge so it's still visible but not crowding. */}
      <div className="mb-3">
        <span
          className={`badge ${isLight ? "badge-light" : "badge-sage"} text-sm px-3.5 py-1.5 tracking-wider inline-block max-w-full`}
        >
          {study.condition}
        </span>
        {study.project_number && (
          <div
            className={`mt-1.5 text-[10px] uppercase tracking-[0.1em] font-medium opacity-70 ${
              isLight ? "text-[var(--text-muted)]" : "text-[var(--gray-light)]"
            }`}
            title={`Reference: ${study.project_number}`}
            data-testid={`study-card-ref-${study.id}`}
          >
            Ref · {study.project_number}
          </div>
        )}
      </div>

      {/* 2. Research-type pills (multi). When the admin selects more
          than one, all of them render as small pills in a row. */}
      {types.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5 mb-5"
          data-testid={`study-format-${study.id}`}
        >
          {types.map((t) => {
            const { Icon: TypeIcon, label } = formatMeta(t);
            return (
              <span
                key={t}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-bold uppercase tracking-[0.06em] ${
                  isLight
                    ? "bg-[var(--light-card)] text-[var(--navy)] border border-[var(--light-border)]"
                    : "bg-[var(--dark-bg)] text-[var(--gray-light)] border border-[var(--dark-border)]"
                }`}
              >
                <TypeIcon size={14} />
                {label}
              </span>
            );
          })}
        </div>
      )}

      {/* 3. Synopsis (clamped) */}
      <p
        className={`text-sm leading-relaxed mb-5 line-clamp-5 flex-1 ${
          isLight ? "text-[var(--text-muted)]" : "text-[var(--gray-light)]"
        }`}
        style={{ minHeight: "calc(1.5em * 5)" }}
      >
        {study.description}
      </p>

      {/* 4. Time commitment */}
      {study.time_commitment && (
        <div
          className={`flex items-center gap-2 text-sm mb-2 ${
            isLight ? "text-[var(--text-muted)]" : "text-[var(--gray-light)]"
          }`}
        >
          <Clock size={14} className={isLight ? "text-[var(--teal)]" : "text-[var(--lime)]"} />
          <span>{study.time_commitment}</span>
        </div>
      )}

      {/* 5. Close date */}
      <div
        className={`flex items-center gap-2 text-sm mb-5 ${
          isLight ? "text-[var(--text-muted)]" : "text-[var(--gray-light)]"
        }`}
      >
        <Calendar size={14} className={isLight ? "text-[var(--teal)]" : "text-[var(--lime)]"} />
        <span>Closes {formatClosingDate(study.end_date)}</span>
      </div>

      {/* 7. Thank-you , stacked compensation, one row per country */}
      <div
        className={`pt-4 mb-4 border-t ${
          isLight ? "border-[var(--light-border)]" : "border-[var(--dark-border)]"
        }`}
      >
        <span
          className={`block text-[10px] uppercase tracking-[0.15em] font-semibold mb-1.5 ${
            isLight ? "text-[var(--text-muted)]" : "text-[var(--gray-light)]"
          }`}
        >
          Thank-you
        </span>
        <CompensationStack
          rows={compRows}
          fallback={compFallback}
          variant={isLight ? "card-light" : "card"}
          testId={`study-card-compensation-${study.id}`}
        />
      </div>

      {/* 8. Find out more */}
      <span
        data-testid={`register-btn-${study.id}`}
        className={`inline-flex items-center gap-1.5 font-semibold text-sm self-start ${
          isLight ? "text-[var(--teal)]" : "text-[var(--lime)]"
        }`}
      >
        Click here to find out more
        <ArrowRight
          size={16}
          className="transition-transform group-hover:translate-x-1"
        />
      </span>
    </button>
  );
};

export const StudyCardSkeleton = ({ variant = "dark" }) => {
  const isLight = variant === "light";
  const base = isLight ? "bg-[var(--light-border)]" : "bg-[var(--dark-border)]";

  return (
    <div
      className={`p-6 md:p-7 rounded-2xl animate-pulse ${
        isLight
          ? "bg-white border border-[var(--light-border)]"
          : "bg-[var(--dark-card)] border border-[var(--dark-border)]"
      }`}
    >
      <div className={`h-5 w-24 ${base} rounded-full mb-3`} />
      <div className={`h-6 w-28 ${base} rounded-full mb-5`} />
      <div className={`h-4 w-full ${base} rounded mb-1`} />
      <div className={`h-4 w-5/6 ${base} rounded mb-1`} />
      <div className={`h-4 w-4/6 ${base} rounded mb-1`} />
      <div className={`h-4 w-5/6 ${base} rounded mb-1`} />
      <div className={`h-4 w-3/6 ${base} rounded mb-5`} />
      <div className={`h-3 w-1/2 ${base} rounded mb-2`} />
      <div className={`h-3 w-1/3 ${base} rounded mb-5`} />
      <div className={`h-10 w-full ${base} rounded`} />
    </div>
  );
};
